/**
 * MerchantWithdrawalRequests — iter-203 (list only; detail on separate page).
 *
 * Route: /dashboard/finance/cloudgm/merchants/withdrawal-requests
 *
 * Compact list view. Clicking any row navigates to the dedicated detail
 * page: /dashboard/finance/cloudgm/merchants/withdrawal-requests/:id
 *
 * Backend endpoints (list only):
 *   GET    /api/admin/withdrawal-requests?status_filter=...
 *   DELETE /api/admin/withdrawal-requests/{id}   (inline delete on hover)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Wallet, Trash2, Loader2, Filter, RefreshCw,
  User, Calendar, Building2, Search, Clock, AlertTriangle,
  ShieldCheck, Shield, BadgeCheck, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { floorAED, fmtAED } from '../utils/money';
import { DirhamPrice } from '../components/DirhamPrice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:  { ar: 'قيد المراجعة', en: 'Pending',  cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  approved: { ar: 'معتمد',        en: 'Approved', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  rejected: { ar: 'مرفوض',        en: 'Rejected', cls: 'bg-red-500/20 text-red-300 border-red-500/40' },
};

const FILTERS = ['all', 'pending', 'approved', 'rejected'];

const normalizeAccountDetails = (ad = {}) => ({
  bankName:       ad.bank_name        || ad.bankName       || ad.bank        || '',
  branch:         ad.branch           || ad.branch_name    || ad.bankBranch  || '',
  iban:           ad.iban             || ad.IBAN           || '',
  accountNumber:  ad.account_number   || ad.accountNumber  || ad.account_no  || '',
  beneficiary:    ad.beneficiary_name || ad.beneficiary    || ad.name        || '',
  swift:          ad.swift_code       || ad.swift          || ad.bic         || '',
});

export default function MerchantWithdrawalRequests() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const isAr = language === 'ar';
  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [pendingCount, setPendingCount] = useState(0);
  const [delayedCount, setDelayedCount] = useState(0);
  const [slaDays, setSlaDays] = useState(15);
  const [delayedOnly, setDelayedOnly] = useState(false);
  const [q, setQ] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (delayedOnly) params.delayed_only = true;
      const res = await axios.get(`${BACKEND_URL}/api/admin/withdrawal-requests`, { headers, params });
      setItems(res.data?.data || []);
      setPendingCount(res.data?.pending_count || 0);
      setDelayedCount(res.data?.delayed_count || 0);
      if (res.data?.sla_days) setSlaDays(res.data.sla_days);
    } catch (err) {
      console.error('Failed to load withdrawal requests:', err);
      toast.error(isAr ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, delayedOnly, headers, isAr]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => {
      const u = it.user || {};
      const ad = normalizeAccountDetails(it.account_details);
      return (
        (u.name || '').toLowerCase().includes(needle) ||
        (u.email || '').toLowerCase().includes(needle) ||
        (u.phone || '').toLowerCase().includes(needle) ||
        (u.bgm_number || '').toLowerCase().includes(needle) ||
        (ad.iban || '').toLowerCase().includes(needle) ||
        String(it.id || '').toLowerCase().includes(needle)
      );
    });
  }, [items, q]);

  const handleOpen = (id) => {
    navigate(`/dashboard/finance/cloudgm/merchants/withdrawal-requests/${id}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isAr ? 'تأكيد حذف هذا الطلب نهائياً؟' : 'Permanently delete this request?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/withdrawal-requests/${id}`, { headers });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Delete failed'));
    }
  };

  return (
    <DashboardLayout
      title="طلبات السحب"
      titleEn="Withdrawal Requests"
      subtitle="إدارة طلبات سحب التجار من المحفظة"
      subtitleEn="Manage merchant wallet withdrawal requests"
      screenCode="FIN-CGM-MERCH-WD"
      backPath="/dashboard/finance/cloudgm/merchants"
      breadcrumb={[
        { labelAr: 'المالية',                labelEn: 'Finance',                       path: '/dashboard/finance' },
        { labelAr: 'CLOUDGM FZ LLC',         labelEn: 'CLOUDGM FZ LLC',                path: '/dashboard/finance/cloudgm' },
        { labelAr: 'منصة التجار (المالية)',  labelEn: 'Merchant Platform (Finance)',   path: '/dashboard/finance/cloudgm/merchants' },
        { labelAr: 'طلبات السحب',            labelEn: 'Withdrawal Requests',           path: null },
      ]}
    >
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        data-testid="withdrawal-requests-page"
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-3">
            <Wallet className="h-6 w-6 text-emerald-400" />
            <div>
              <h2 className="text-lg font-bold text-white">
                {isAr ? 'طلبات السحب الواردة من منصة التجار' : 'Incoming Merchant Withdrawal Requests'}
              </h2>
              <p className="text-xs text-[#71717A] mt-0.5">
                {isAr
                  ? 'اضغط على أي طلب للانتقال إلى صفحة التفاصيل الكاملة ومطابقة الإيصال بالذكاء الاصطناعي'
                  : 'Click any row to open the full detail page with AI receipt matching'}
              </p>
            </div>
            {pendingCount > 0 && (
              <span
                className="ms-2 inline-flex items-center px-3 py-1 rounded-full text-xs bg-amber-500/15 text-amber-300 border border-amber-500/30"
                data-testid="withdrawal-pending-count"
              >
                {pendingCount} {isAr ? 'قيد المراجعة' : 'pending'}
              </span>
            )}
            {delayedCount > 0 && (
              <button
                onClick={() => setDelayedOnly((v) => !v)}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs border transition-colors ${
                  delayedOnly
                    ? 'bg-red-500/25 text-red-200 border-red-500/50'
                    : 'bg-red-500/10 text-red-300 border-red-500/30 hover:bg-red-500/20'
                }`}
                title={isAr
                  ? `${delayedCount} طلب تجاوز الـ${slaDays} يوم`
                  : `${delayedCount} exceeded the ${slaDays}-day SLA`}
                data-testid="withdrawal-delayed-badge"
              >
                <AlertTriangle className="h-3 w-3" />
                {delayedCount} {isAr ? 'متأخر' : 'delayed'}
              </button>
            )}
          </div>
          <Button
            onClick={fetchItems}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/5"
            data-testid="withdrawal-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 me-2" />
            {isAr ? 'تحديث' : 'Refresh'}
          </Button>
        </div>

        {/* Filters + search */}
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="withdrawal-filter-bar">
          <Filter className="h-4 w-4 text-[#71717A]" />
          {FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors border ${
                statusFilter === s
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                  : 'bg-white/5 text-[#A1A1AA] border-white/10 hover:text-white'
              }`}
              data-testid={`withdrawal-filter-${s}`}
            >
              {s === 'all'
                ? (isAr ? 'الكل' : 'All')
                : (isAr ? STATUS_META[s].ar : STATUS_META[s].en)}
            </button>
          ))}
          <div className="flex-1" />
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute top-1/2 -translate-y-1/2 start-2.5 text-[#71717A]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={isAr ? 'ابحث بالاسم / الإيميل / IBAN…' : 'Search name / email / IBAN…'}
              className="ps-8 pe-3 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-white w-56 focus:outline-none focus:border-emerald-500/40"
              data-testid="withdrawal-search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="withdrawal-loading">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState isAr={isAr} statusFilter={statusFilter} />
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden bg-white/[0.02] backdrop-blur-xl">
            <div
              className="grid grid-cols-[minmax(200px,1.6fr)_minmax(140px,1fr)_minmax(140px,1fr)_130px_130px_120px_60px] gap-3 px-4 py-2.5 border-b border-white/10 text-[10px] uppercase tracking-wide text-[#71717A] font-medium"
              data-testid="withdrawal-list-header"
            >
              <div>{isAr ? 'التاجر' : 'Merchant'}</div>
              <div>{isAr ? 'التصنيف' : 'Classification'}</div>
              <div className="text-end">{isAr ? 'المبلغ' : 'Amount'}</div>
              <div>{isAr ? 'الطريقة' : 'Method'}</div>
              <div>{isAr ? 'SLA' : 'SLA'}</div>
              <div>{isAr ? 'الحالة' : 'Status'}</div>
              <div />
            </div>

            {filtered.map((item, idx) => (
              <WithdrawalRow
                key={item.id}
                item={item}
                idx={idx}
                isAr={isAr}
                slaDays={slaDays}
                onOpen={() => handleOpen(item.id)}
                onDelete={() => handleDelete(item.id)}
              />
            ))}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}

// ─── list row ───────────────────────────────────────────────────────
function WithdrawalRow({ item, idx, isAr, slaDays, onOpen, onDelete }) {
  const status = STATUS_META[item.status] || STATUS_META.pending;
  const u = item.user || {};
  const amount = floorAED(item.amount);
  const dateStr = useMemo(() => {
    if (!item.created_at) return '—';
    try { return new Date(item.created_at).toLocaleDateString(isAr ? 'ar-AE' : 'en-GB'); }
    catch { return item.created_at; }
  }, [item.created_at, isAr]);

  // iter-207 — SLA presentation for THIS row
  const isPending = item.status === 'pending';
  const isDelayed = !!item.is_delayed;
  const daysRemaining = typeof item.days_remaining === 'number' ? item.days_remaining : null;

  let slaCls = 'text-[#71717A]';
  let slaIcon = Clock;
  let slaText = '—';
  if (isPending && daysRemaining !== null) {
    if (isDelayed) {
      slaCls = 'text-red-300 font-semibold';
      slaIcon = AlertTriangle;
      slaText = isAr
        ? `متأخر ${Math.abs(daysRemaining)} يوم`
        : `${Math.abs(daysRemaining)}d overdue`;
    } else if (daysRemaining <= 3) {
      slaCls = 'text-amber-300 font-medium';
      slaText = isAr ? `باقٍ ${daysRemaining} يوم` : `${daysRemaining}d left`;
    } else {
      slaCls = 'text-cyan-300';
      slaText = isAr ? `باقٍ ${daysRemaining} يوم` : `${daysRemaining}d left`;
    }
  }
  // iter-209 — SLA countdown is only meaningful for pending requests.
  // For approved / rejected / cancelled rows we simply show "—" instead of
  // "بعد 0 يوم" which is confusing to admins.
  const SlaIcon = slaIcon;

  return (
    <motion.button
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(idx * 0.015, 0.25) }}
      type="button"
      onClick={onOpen}
      className={`w-full grid grid-cols-[minmax(200px,1.6fr)_minmax(140px,1fr)_minmax(140px,1fr)_130px_130px_120px_60px] gap-3 px-4 py-3 border-b border-white/5 text-start hover:bg-emerald-500/[0.04] transition-colors ${
        isDelayed ? 'bg-red-500/[0.03]' : ''
      }`}
      data-testid={`withdrawal-row-${item.id}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-7 w-7 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <User className="h-3.5 w-3.5 text-emerald-300" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-white text-sm font-medium truncate" data-testid={`wd-row-name-${item.id}`}>
              {u.name || (isAr ? '— تاجر بدون اسم —' : '— Unnamed —')}
            </div>
            <div className="text-[10px] text-[#71717A] truncate">
              {u.email || u.phone || dateStr}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-wrap self-center">
        <AgentBadges user={u} isAr={isAr} compact />
      </div>

      <div className="text-end self-center">
        <div className="text-white text-sm font-semibold tabular-nums" data-testid={`wd-row-amount-${item.id}`}>
          {fmtAED(amount)}
        </div>
        <div className="text-[10px] text-[#71717A]">{item.currency || 'AED'}</div>
      </div>

      <div className="text-[11px] text-[#A1A1AA] self-center truncate">
        {item.method || '—'}
      </div>

      {/* iter-207 — SLA column */}
      <div className={`text-[11px] self-center flex items-center gap-1 ${slaCls}`}
           title={isAr ? `مدة الالتزام ${slaDays} يوم من تاريخ الطلب` : `${slaDays}-day SLA`}
           data-testid={`wd-row-sla-${item.id}`}>
        <SlaIcon className="h-3 w-3 shrink-0" />
        <span className="truncate">{slaText}</span>
      </div>

      <div className="self-center">
        <span
          className={`px-2 py-0.5 rounded-full text-[10px] border ${status.cls}`}
          data-testid={`wd-row-status-${item.id}`}
        >
          {isAr ? status.ar : status.en}
        </span>
      </div>

      <div className="self-center flex items-center justify-end gap-1">
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onDelete();
            }
          }}
          className="p-1 rounded hover:bg-red-500/10 text-red-300/60 hover:text-red-300 transition-colors cursor-pointer"
          data-testid={`wd-row-delete-${item.id}`}
          title={isAr ? 'حذف' : 'Delete'}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </span>
        {isAr
          ? <ChevronLeft className="h-4 w-4 text-[#71717A]" />
          : <ChevronRight className="h-4 w-4 text-[#71717A]" />}
      </div>
    </motion.button>
  );
}

// ─── Agent classification badges ───────────────────────────────────
// iter-208 — always show account type + KYC verification (positive AND negative).
function AgentBadges({ user, isAr, compact = false }) {
  if (!user) return <span className="text-[10px] text-[#71717A]">—</span>;
  const badges = [];

  if (user.is_verified_agent) {
    badges.push({ key: 'verified-agent', icon: BadgeCheck, label: isAr ? 'وكيل موثّق' : 'Verified Agent',
      cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' });
  } else if (user.is_agent) {
    badges.push({ key: 'agent', icon: Shield, label: isAr ? 'وكيل غير موثّق' : 'Agent (unverified)',
      cls: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' });
  } else {
    const role = (user.role || '').toLowerCase();
    if (role === 'business' || role === 'merchant') {
      badges.push({ key: 'merchant', icon: Building2, label: isAr ? 'تاجر' : 'Merchant',
        cls: 'bg-amber-500/15 text-amber-200 border-amber-500/30' });
    } else if (role === 'customer') {
      badges.push({ key: 'customer', icon: User, label: isAr ? 'عميل' : 'Customer',
        cls: 'bg-slate-500/15 text-slate-200 border-slate-500/30' });
    } else {
      badges.push({ key: 'merchant-default', icon: Building2, label: isAr ? 'تاجر' : 'Merchant',
        cls: 'bg-amber-500/15 text-amber-200 border-amber-500/30' });
    }
  }

  if (user.kyc_verified) {
    badges.push({ key: 'kyc-ok', icon: ShieldCheck, label: isAr ? 'KYC ✓' : 'KYC ✓',
      cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' });
  } else if (user._source !== 'bank_beneficiary') {
    badges.push({ key: 'kyc-no', icon: AlertTriangle, label: isAr ? 'KYC غير موثّق' : 'KYC no',
      cls: 'bg-red-500/15 text-red-300 border-red-500/30' });
  }

  if (badges.length === 0) return <span className="text-[10px] text-[#71717A]">—</span>;
  return (
    <>
      {badges.map((b) => (
        <span
          key={b.key}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-medium ${b.cls}`}
          data-testid={`wd-badge-${b.key}`}
        >
          <b.icon className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
          {b.label}
        </span>
      ))}
    </>
  );
}

function EmptyState({ isAr, statusFilter }) {
  return (
    <div
      className="text-center py-20 bg-white/[0.02] border border-white/10 rounded-2xl"
      data-testid="withdrawal-empty"
    >
      <Wallet className="h-14 w-14 mx-auto mb-4 text-emerald-400/30" />
      <h3 className="text-white font-medium mb-1">
        {isAr ? 'لا توجد طلبات سحب' : 'No withdrawal requests'}
      </h3>
      <p className="text-xs text-[#71717A]">
        {statusFilter === 'all'
          ? (isAr ? 'لم يصل أي طلب سحب من منصة التجار حتى الآن' : 'No requests received from the merchant platform yet')
          : (isAr ? `لا توجد طلبات بهذه الحالة: ${statusFilter}` : `No requests with status: ${statusFilter}`)}
      </p>
    </div>
  );
}
